'use client';

import { useMemo } from 'react';
import {
  MessageCircle,
  Phone,
  CalendarClock,
  Mail,
  MessagesSquare,
  ArrowUpRight,
} from 'lucide-react';
import { useContactMailto } from '@/features/help/hooks/use-contact-mailto';
import { getSupportConfig, isWithinSupportHours } from '../lib/support-config';
import { useSupportContext } from '../hooks/use-support-context';

interface SupportContactOptionsProps {
  onStartLiveChat: () => void;
  onScheduleCall: () => void;
}

export function SupportContactOptions({
  onStartLiveChat,
  onScheduleCall,
}: SupportContactOptionsProps) {
  const config = getSupportConfig();
  const { buildMailto } = useContactMailto();
  const { snapshot } = useSupportContext();
  const online = isWithinSupportHours();

  const whatsappHref = useMemo(() => {
    if (!config.whatsappE164) return null;
    const ctx = snapshot();
    const text = [
      'Salut! Am o întrebare despre SoftFactura.',
      '',
      `Pagina: ${ctx.url || ctx.pathname}`,
      ctx.company ? `Firmă: ${ctx.company.name}` : '',
      ctx.user ? `Cont: ${ctx.user.email}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    const phone = config.whatsappE164.replace(/[^\d]/g, '');
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  }, [config.whatsappE164, snapshot]);

  const phoneHref = config.phoneE164 ? `tel:${config.phoneE164}` : null;
  const phoneDisplay = config.phoneDisplay ?? config.phoneE164;

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={onStartLiveChat}
        className="group flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3 text-left transition-all hover:border-amber-300 hover:bg-amber-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-amber-700/50 dark:hover:bg-amber-950/20"
      >
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 text-amber-700 transition-colors group-hover:bg-amber-200 dark:bg-amber-950/50 dark:text-amber-300">
          <MessagesSquare className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <span className="flex-1">
          <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Chat in-app
          </span>
          <span className="block text-[0.75rem] text-neutral-500 dark:text-neutral-500">
            Răspuns direct în cont · {online ? 'online acum' : config.supportHours}
          </span>
        </span>
        <ArrowUpRight
          className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-amber-600 dark:text-neutral-700 dark:group-hover:text-amber-400"
          strokeWidth={1.75}
        />
      </button>

      {whatsappHref && (
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3 transition-all hover:border-emerald-300 hover:bg-emerald-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-emerald-700/50 dark:hover:bg-emerald-950/20"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 transition-colors group-hover:bg-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300">
            <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
              WhatsApp
            </span>
            <span className="block text-[0.75rem] text-neutral-500 dark:text-neutral-500">
              {config.whatsappE164}
            </span>
          </span>
          <ArrowUpRight
            className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-emerald-600 dark:text-neutral-700 dark:group-hover:text-emerald-400"
            strokeWidth={1.75}
          />
        </a>
      )}

      {phoneHref && (
        <a
          href={phoneHref}
          className="group flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3 transition-all hover:border-sky-300 hover:bg-sky-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-sky-700/50 dark:hover:bg-sky-950/20"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-700 transition-colors group-hover:bg-sky-200 dark:bg-sky-950/50 dark:text-sky-300">
            <Phone className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Telefon
            </span>
            <span className="block text-[0.75rem] text-neutral-500 dark:text-neutral-500">
              {phoneDisplay} · {config.supportHours}
            </span>
          </span>
          <ArrowUpRight
            className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-sky-600 dark:text-neutral-700 dark:group-hover:text-sky-400"
            strokeWidth={1.75}
          />
        </a>
      )}

      {config.calComUsername && (
        <button
          type="button"
          onClick={onScheduleCall}
          className="group flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3 text-left transition-all hover:border-violet-300 hover:bg-violet-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-violet-700/50 dark:hover:bg-violet-950/20"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 text-violet-700 transition-colors group-hover:bg-violet-200 dark:bg-violet-950/50 dark:text-violet-300">
            <CalendarClock className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <span className="flex-1">
            <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Programează apel
            </span>
            <span className="block text-[0.75rem] text-neutral-500 dark:text-neutral-500">
              15 minute · alegi tu intervalul
            </span>
          </span>
          <ArrowUpRight
            className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-violet-600 dark:text-neutral-700 dark:group-hover:text-violet-400"
            strokeWidth={1.75}
          />
        </button>
      )}

      <a
        href={buildMailto()}
        className="group flex w-full items-center gap-3 rounded-xl border border-neutral-200 bg-white px-3.5 py-3 transition-all hover:border-neutral-400 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-600 dark:hover:bg-neutral-800"
      >
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700 transition-colors group-hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300">
          <Mail className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <span className="flex-1">
          <span className="block text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Email
          </span>
          <span className="block text-[0.75rem] text-neutral-500 dark:text-neutral-500">
            {config.contactEmail} · răspuns {config.responseSla}
          </span>
        </span>
        <ArrowUpRight
          className="h-4 w-4 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-neutral-600 dark:text-neutral-700 dark:group-hover:text-neutral-300"
          strokeWidth={1.75}
        />
      </a>
    </div>
  );
}
